import {
  NumberInput,
  required,
  TextInput,
  BooleanInput,
  useTranslate,
  useInput,
} from 'react-admin';
import { Box, Stack, Typography } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { LottoDrawConfig } from '../../../services/redpacketService';

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ_UTC_PLUS_8 = 'Asia/Shanghai';

/** UTC "HH:mm" -> UTC+8 dayjs for display */
const utcTimeToUtc8 = (utcTime: string) => {
  if (!utcTime) return null;
  return dayjs.utc(`2000-01-01T${utcTime}:00`).tz(TZ_UTC_PLUS_8);
};

/** UTC+8 dayjs/time string -> UTC "HH:mm" for API (将用户选择的时间视为 UTC+8) */
const utc8ToUtcTime = (d: dayjs.Dayjs | string | null): string => {
  if (!d) return '';
  const d8 =
    typeof d === 'string'
      ? dayjs.tz(`2000-01-01T${d}`, TZ_UTC_PLUS_8)
      : dayjs.tz(`2000-01-01T${String(d.hour()).padStart(2, '0')}:${String(d.minute()).padStart(2, '0')}:00`, TZ_UTC_PLUS_8);
  return d8.utc().format('HH:mm');
};

interface FormProps {
  isCreate: boolean;
  record?: LottoDrawConfig;
}

const LottoDrawConfigForm = (props: FormProps) => {
  const { isCreate, record } = props;
  const t = useTranslate();

  const numberProps = {
    parse: (v: any) => (v === '' || v == null ? undefined : Number(v)),
    format: (v: any) => (v == null ? '' : v),
    inputProps: {
      inputMode: 'decimal',
      step: 'any',
      onWheel: (e: React.WheelEvent<HTMLInputElement>) => {
        (e.currentTarget as HTMLInputElement).blur();
      },
    } as const,
  } as const;

  const {
    field,
    fieldState: { error },
  } = useInput({
    source: 'drawTimeUtc',
    validate: required(),
    defaultValue: record?.drawTimeUtc ?? '12:00',
  });

  return (
    <Stack spacing={3} gap={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        mb={1}
      >
        <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
          {t('module.redpacket.drawTimeUtc8')}
        </Typography>
        <Box sx={{ flexGrow: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimePicker
              value={field.value ? utcTimeToUtc8(field.value) : null}
              onChange={(v) => field.onChange(v ? utc8ToUtcTime(v) : '')}
              format="HH:mm"
              ampm={false}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  error: !!error,
                  helperText: error?.message,
                },
              }}
            />
          </LocalizationProvider>
        </Box>
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        mb={1}
      >
        <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
          {t('module.redpacket.prizeAmount')}
        </Typography>
        <Box sx={{ flexGrow: 1 }}>
          <NumberInput
            label={false}
            source="prizeAmount"
            validate={[required()]}
            placeholder={t('module.redpacket.prizeAmountPlaceholder')}
            fullWidth
            defaultValue={record?.prizeAmount}
            {...numberProps}
          />
        </Box>
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        mb={1}
      >
        <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
          {t('module.redpacket.totalBudget')}
        </Typography>
        <Box sx={{ flexGrow: 1 }}>
          <NumberInput
            label={false}
            source="totalBudget"
            validate={[required()]}
            placeholder={t('module.redpacket.totalBudgetPlaceholder')}
            fullWidth
            defaultValue={record?.totalBudget}
            {...numberProps}
          />
        </Box>
      </Stack>

      {!isCreate && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
        <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
          {t('module.redpacket.remainingBudget')}
        </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="remainingBudget"
              placeholder={t('module.redpacket.remainingBudgetPlaceholder')}
              fullWidth
              defaultValue={record?.remainingBudget}
              {...numberProps}
            />
          </Box>
        </Stack>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        mb={1}
      >
        <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
          {t('module.redpacket.fundPoolAddress')}
        </Typography>
        <Box sx={{ flexGrow: 1 }}>
          <TextInput
            label={false}
            source="fundPoolAddress"
            validate={[required()]}
            placeholder={t('module.redpacket.fundPoolAddressPlaceholder')}
            fullWidth
            defaultValue={record?.fundPoolAddress}
          />
        </Box>
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        mb={1}
      >
        <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
          {t('module.redpacket.eligibilityThreshold')}
        </Typography>
        <Box sx={{ flexGrow: 1 }}>
          <NumberInput
            label={false}
            source="eligibilityThreshold"
            validate={[required()]}
            placeholder={t('module.redpacket.eligibilityThresholdPlaceholder')}
            fullWidth
            defaultValue={record?.eligibilityThreshold}
            {...numberProps}
          />
        </Box>
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        mb={1}
      >
        <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
          {t('module.redpacket.isActive')}
        </Typography>
        <Box sx={{ flexGrow: 1 }}>
          <BooleanInput label="" source="isActive" defaultValue={record?.isActive ?? true} />
        </Box>
      </Stack>
    </Stack>
  );
};

export default LottoDrawConfigForm;
